import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Paintbrush, Lock, X, House, Palette, Triangle, Fence, Check, Coins } from "lucide-react";
import { api } from "../../lib/api";
import { GLASS, FONT, glassInner } from "../kota/ui";
import { chipStyle } from "../kota/Sheet";
import { PanelBawah } from "./RumahUI";
import { tombolAksen } from "./KebunMode";

const TABS = [["rumah", House, "Rumah"], ["dinding", Palette, "Dinding"], ["atap", Triangle, "Atap"], ["pagar", Fence, "Pagar"]];   // barang halaman → mode Tata halaman
const chip = (on) => ({ ...glassInner(on), border: `1px solid ${on ? "var(--g-accent)" : "var(--g-border)"}`, color: on ? GLASS.accent : GLASS.ink, borderRadius: 16, padding: "8px 12px", display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", fontFamily: FONT, flex: "none", minWidth: 72, whiteSpace: "nowrap", transition: "background-color .2s, color .2s, border-color .2s" });

// Panel Hias ringkas (gaya kaca Jawsel): perubahan dipratinjau langsung di kanvas (onPreview), baru diterapkan saat "Kunci".
// Kunci pertama gratis untuk model/dinding/atap; selanjutnya bayar per bagian yang berubah. Pagar dibeli sekali. Barang halaman: mode Tata halaman.
export function HiasPanel({ token, rumah, katalog, saldo, onPreview, onUpdate, onClose }) {
  const [tab, setTab] = useState("rumah"), [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ model: rumah.model, atap: rumah.atap, dinding: rumah.dinding, dipasang: [...rumah.dipasang] });
  const dekorMap = useMemo(() => Object.fromEntries(katalog.dekor.map(d => [d.key, d])), [katalog]);

  const ubah = patch => { const d = { ...draft, ...patch }; setDraft(d); onPreview(d); };
  const togglePasang = key => {
    const kat = dekorMap[key].kat;
    let dipasang = draft.dipasang.filter(x => x !== key && (kat !== "pagar" || dekorMap[x].kat !== "pagar"));   // satu pagar saja
    if (!draft.dipasang.includes(key)) dipasang = [...dipasang, key];
    ubah({ dipasang });
  };

  const bagian = ["model", "atap", "dinding"].filter(k => draft[k] !== rumah[k]);
  const barangBaru = draft.dipasang.filter(x => !rumah.dekor.includes(x));
  const biaya = (rumah.hias.gratis ? 0 : bagian.reduce((a, k) => a + katalog.hias_harga[k], 0)) + barangBaru.reduce((a, x) => a + dekorMap[x].harga, 0);
  const berubah = bagian.length > 0 || draft.dipasang.join() !== rumah.dipasang.join();
  const mampu = saldo >= biaya;

  const kunci = async () => {
    setBusy(true);
    try {
      const body = { dipasang: draft.dipasang }; for (const k of bagian) body[k] = draft[k];
      const r = await api.rumahHias(token, body); onUpdate(r); onPreview(null);
      toast.success(r.biaya ? `Perubahan dikunci · ${r.biaya} koin` : "Perubahan dikunci · gratis"); onClose();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const batal = () => { onPreview(null); onClose(); };

  const swatch = (warna, k) => (
    <button key={warna} data-testid={`${k}-${warna.slice(1)}`} aria-label={warna} onClick={() => ubah({ [k]: warna })} style={{ width: 40, height: 40, borderRadius: 14, border: draft[k] === warna ? "3px solid var(--g-accent)" : "2px solid rgba(255,255,255,.85)", background: warna, cursor: "pointer", flex: "none", display: "grid", placeItems: "center", boxShadow: "0 3px 8px rgba(20,20,20,.12)" }}>
      {draft[k] === warna && <Check size={16} color="#fff" strokeWidth={3} style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.5))" }} />}
    </button>
  );
  const barang = d => {
    const on = draft.dipasang.includes(d.key), punya = rumah.dekor.includes(d.key);
    return (
      <button key={d.key} data-testid={`dekor-${d.key}`} aria-pressed={on} onClick={() => togglePasang(d.key)} style={chip(on)}>
        <b style={{ fontSize: 12 }}>{d.nama}</b>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: on ? GLASS.accent : GLASS.sub, display: "inline-flex", alignItems: "center", gap: 3 }}>
          {punya ? (on ? <><Check size={10} /> Terpasang</> : "Dimiliki") : <><Coins size={10} /> {d.harga}</>}
        </span>
      </button>
    );
  };

  return (
    <PanelBawah testid="hias-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 4px 10px" }}>
        <span style={{ width: 34, height: 34, borderRadius: 12, background: GLASS.accentBg, color: GLASS.accent, display: "grid", placeItems: "center", flex: "none" }}><Paintbrush size={16} /></span>
        <b style={{ fontSize: 14, fontWeight: 800, letterSpacing: -0.2 }}>Hias rumah</b>
        <span data-testid="hias-biaya" style={{ ...chipStyle(!berubah ? "gray" : biaya === 0 ? "green" : mampu ? "blue" : "red"), display: "inline-flex", alignItems: "center", gap: 4 }}>
          {!berubah ? "Belum ada ubahan" : biaya === 0 ? "Gratis" : <><Coins size={10} /> {biaya} koin</>}
        </span>
        <span style={{ flex: 1 }} />
        <button data-testid="hias-batal" aria-label="Batal" onClick={batal} style={{ width: 34, height: 34, borderRadius: 99, border: 0, background: GLASS.inner, color: GLASS.ink, display: "grid", placeItems: "center", cursor: "pointer" }}><X size={15} /></button>
        <button data-testid="hias-kunci" disabled={busy || !berubah || !mampu} onClick={kunci} style={{ ...tombolAksen, opacity: busy || !berubah || !mampu ? .5 : 1 }}><Lock size={13} /> Kunci</button>
      </div>

      <div data-testid={`hias-isi-${tab}`} style={{ display: "flex", gap: 6, overflowX: "auto", padding: "2px 4px 6px", scrollbarWidth: "none", alignItems: "center", minHeight: 58 }}>
        {tab === "rumah" && katalog.model.map(m => (
          <button key={m.key} data-testid={`model-${m.key}`} aria-pressed={draft.model === m.key} onClick={() => ubah({ model: m.key })} style={chip(draft.model === m.key)}>
            <House size={16} strokeWidth={1.8} /><b style={{ fontSize: 11.5 }}>{m.nama}</b>
          </button>
        ))}
        {tab === "dinding" && katalog.warna_dinding.map(w => swatch(w, "dinding"))}
        {tab === "atap" && katalog.warna_atap.map(w => swatch(w, "atap"))}
        {tab === "pagar" && <>
          <button data-testid="dekor-pagar_kayu" aria-pressed={!draft.dipasang.some(x => dekorMap[x].kat === "pagar")} onClick={() => ubah({ dipasang: draft.dipasang.filter(x => dekorMap[x].kat !== "pagar") })} style={chip(!draft.dipasang.some(x => dekorMap[x].kat === "pagar"))}>
            <b style={{ fontSize: 12 }}>Pagar kayu</b><span style={{ fontSize: 10.5, fontWeight: 700, color: GLASS.sub }}>bawaan</span>
          </button>
          {katalog.dekor.filter(d => d.kat === "pagar").map(barang)}
        </>}
      </div>

      <nav style={{ display: "grid", gridTemplateColumns: `repeat(${TABS.length}, 1fr)`, gap: 2, marginTop: 4 }}>
        {TABS.map(([key, Icon, label]) => {
          const on = tab === key;
          return (
            <button key={key} data-testid={`hias-tab-${key}`} aria-pressed={on} onClick={() => setTab(key)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, borderRadius: 16, cursor: "pointer", padding: "7px 0 6px", ...glassInner(on), color: on ? GLASS.accent : GLASS.ink, fontFamily: FONT, WebkitTapHighlightColor: "transparent" }}>
              <Icon size={19} strokeWidth={1.8} /><span style={{ fontSize: 10, fontWeight: on ? 700 : 600 }}>{label}</span>
            </button>
          );
        })}
      </nav>
    </PanelBawah>
  );
}
